/**
 * B2B Phase C — Documents post-livraison (Facture / Ticket / Autre).
 *
 * Affiché dans le détail d'un dossier B2B. Permet aux admins :
 *   - d'uploader un PDF/image,
 *   - de l'envoyer à la clinique via WhatsApp (lien wa.me) ou Email
 *     (Resend, PDF en pièce jointe),
 *   - de copier le lien public sécurisé,
 *   - de supprimer (soft-delete) un document.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FileText, Upload, Trash2, Mail, MessageCircle, Copy, Loader2, Download, Send,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../../ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const auth = () => {
  const t = localStorage.getItem('dermascan_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const DOC_TYPE_LABELS = {
  facture: 'Facture',
  ticket: 'Ticket de caisse',
  autre: 'Autre',
};
const DOC_TYPE_TONES = {
  facture: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  ticket: 'bg-sky-100 text-sky-700 border-sky-200',
  autre: 'bg-zinc-100 text-zinc-700 border-zinc-200',
};

const fmtSize = (bytes) => {
  if (!bytes) return '—';
  const kb = bytes / 1024;
  return kb < 1024 ? `${kb.toFixed(0)} Ko` : `${(kb / 1024).toFixed(1)} Mo`;
};

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const B2BDocumentsSection = ({ order, canWrite }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState('facture');
  const [docLabel, setDocLabel] = useState('');
  const fileInputRef = useRef(null);

  const [sendModal, setSendModal] = useState(null); // doc or null
  const [sendChannel, setSendChannel] = useState('both');
  const [sendEmail, setSendEmail] = useState('');
  const [sendPhone, setSendPhone] = useState('');
  const [sendBusy, setSendBusy] = useState(false);

  const reload = useCallback(async () => {
    if (!order?.id) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/b2b/orders/${order.id}/documents`, { headers: auth() });
      setItems(res.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur chargement documents');
    } finally {
      setLoading(false);
    }
  }, [order?.id]);

  useEffect(() => { reload(); }, [reload]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 10 Mo).');
      e.target.value = '';
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('doc_type', docType);
      if (docLabel.trim()) fd.append('label', docLabel.trim());
      await axios.post(`${API}/b2b/orders/${order.id}/documents`, fd, {
        headers: { ...auth(), 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`${DOC_TYPE_LABELS[docType]} importé.`);
      setDocLabel('');
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Échec de l\'upload');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const onDelete = async (doc) => {
    if (!window.confirm(`Supprimer "${doc.file_name}" ?`)) return;
    try {
      await axios.delete(`${API}/b2b/documents/${doc.id}`, { headers: auth() });
      toast.success('Document supprimé.');
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur suppression');
    }
  };

  const onDownload = async (doc) => {
    try {
      const res = await axios.get(`${API}/b2b/documents/${doc.id}/download`, {
        headers: auth(), responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: doc.content_type }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.file_name || 'document.pdf');
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 2000);
    } catch (e) {
      toast.error('Erreur téléchargement');
    }
  };

  const openSend = (doc) => {
    setSendModal(doc);
    setSendChannel('both');
    setSendEmail('');
    setSendPhone('');
  };

  const onSend = async () => {
    if (!sendModal) return;
    setSendBusy(true);
    try {
      const body = { channel: sendChannel };
      if (sendEmail.trim()) body.email = sendEmail.trim();
      if (sendPhone.trim()) body.phone = sendPhone.trim();
      const res = await axios.post(`${API}/b2b/documents/${sendModal.id}/send`, body, { headers: auth() });
      const errs = res.data?.errors || [];
      if (res.data?.email_sent) toast.success('Email envoyé à la clinique.');
      if (res.data?.whatsapp_url) {
        window.open(res.data.whatsapp_url, '_blank', 'noopener,noreferrer');
      }
      errs.forEach((m) => toast.warning(m, { duration: 6000 }));
      if (!errs.length && !res.data?.email_sent && !res.data?.whatsapp_url) {
        toast.info('Aucun canal n\'a pu être déclenché.');
      }
      setSendModal(null);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur envoi');
    } finally {
      setSendBusy(false);
    }
  };

  const onCopyLink = (doc) => {
    if (!doc.public_url) return;
    try {
      navigator.clipboard.writeText(doc.public_url);
      toast.success('Lien public copié.');
    } catch {
      toast.error('Copie indisponible');
    }
  };

  return (
    <div data-testid="b2b-documents-section">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <h3 className="text-sm font-bold text-zinc-700 flex items-center gap-1">
          <FileText className="w-4 h-4" /> Documents post-livraison
          <Badge className="bg-zinc-100 text-zinc-700 border border-zinc-200 ml-2">{items.length}</Badge>
        </h3>
      </div>

      {canWrite && (
        <div className="rounded-md border border-dashed border-zinc-300 bg-zinc-50 p-3 mb-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Type</label>
              <select
                value={docType}
                onChange={(e) => setDocType(e.target.value)}
                className="w-full border border-zinc-300 rounded-md p-2 text-sm h-10"
                data-testid="b2b-doc-type-select"
              >
                <option value="facture">Facture</option>
                <option value="ticket">Ticket de caisse</option>
                <option value="autre">Autre</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Libellé (optionnel)</label>
              <Input
                value={docLabel}
                onChange={(e) => setDocLabel(e.target.value)}
                placeholder="Ex. Facture Mediciel n° F1234"
                data-testid="b2b-doc-label"
              />
            </div>
            <div className="flex items-end">
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf,image/jpeg,image/png,image/webp"
                onChange={onUpload}
                disabled={uploading}
                className="hidden"
                data-testid="b2b-doc-file-input"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="bg-[#1B4D3E] hover:bg-[#1B4D3E]/90 text-white w-full"
                data-testid="b2b-doc-upload-btn"
              >
                {uploading
                  ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  : <Upload className="w-4 h-4 mr-2" />}
                Téléverser le document
              </Button>
            </div>
          </div>
          <p className="text-xs text-zinc-500 mt-2">PDF / JPG / PNG / WEBP — max 10 Mo.</p>
        </div>
      )}

      <div className="space-y-2">
        {items.map((doc) => (
          <div
            key={doc.id}
            className="border border-zinc-200 rounded-md p-3 hover:bg-zinc-50"
            data-testid={`b2b-doc-row-${doc.id}`}
          >
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={`${DOC_TYPE_TONES[doc.doc_type]} border`}>
                    {DOC_TYPE_LABELS[doc.doc_type]}
                  </Badge>
                  <span className="font-medium text-zinc-900 truncate">{doc.label || doc.file_name}</span>
                  <span className="text-xs text-zinc-400">{fmtSize(doc.file_size)}</span>
                </div>
                <div className="text-xs text-zinc-500 mt-1">
                  Téléversé le {fmtDateTime(doc.uploaded_at)} par {doc.uploaded_by_name || '—'}
                </div>
                {!!(doc.sent_log || []).length && (
                  <ul className="text-xs text-emerald-700 mt-1 space-y-0.5">
                    {(doc.sent_log || []).slice(-3).map((s, i) => (
                      <li key={i}>
                        ✓ Envoyé via <strong>{s.channel}</strong>
                        {s.email && ` à ${s.email}`}
                        {s.phone && ` (${s.phone})`}
                        {' — '}{fmtDateTime(s.at)}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {canWrite && (
                <div className="flex items-center gap-1 flex-wrap">
                  <Button
                    size="sm"
                    onClick={() => openSend(doc)}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid={`b2b-doc-send-${doc.id}`}
                  >
                    <Send className="w-3.5 h-3.5 mr-1" /> Envoyer
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => onCopyLink(doc)} data-testid={`b2b-doc-copy-${doc.id}`}>
                    <Copy className="w-3.5 h-3.5 mr-1" /> Lien
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => onDownload(doc)} data-testid={`b2b-doc-dl-${doc.id}`}>
                    <Download className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onDelete(doc)}
                    className="border-red-200 text-red-700 hover:bg-red-50"
                    data-testid={`b2b-doc-del-${doc.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        ))}
        {!items.length && (
          <p className="text-sm text-zinc-400 italic" data-testid="b2b-documents-empty">
            {loading ? 'Chargement…' : 'Aucun document — téléversez la facture ou le ticket dès qu\'il est disponible.'}
          </p>
        )}
      </div>

      {sendModal && (
        <Dialog open={!!sendModal} onOpenChange={(o) => !o && setSendModal(null)}>
          <DialogContent className="max-w-md" data-testid="b2b-doc-send-dialog">
            <DialogHeader>
              <DialogTitle>Envoyer « {sendModal.label || sendModal.file_name} »</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">Canal</label>
                <div className="flex gap-2">
                  {[
                    { id: 'email', label: 'Email', icon: Mail },
                    { id: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
                    { id: 'both', label: 'Les deux', icon: Send },
                  ].map((c) => {
                    const Icon = c.icon;
                    const active = sendChannel === c.id;
                    return (
                      <button
                        key={c.id}
                        onClick={() => setSendChannel(c.id)}
                        className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-sm border transition ${
                          active
                            ? 'bg-[#1B4D3E] text-white border-[#1B4D3E]'
                            : 'bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50'
                        }`}
                        data-testid={`b2b-doc-channel-${c.id}`}
                      >
                        <Icon className="w-4 h-4" /> {c.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              {(sendChannel === 'email' || sendChannel === 'both') && (
                <div>
                  <label className="text-xs text-zinc-500 mb-1 block">Email destinataire (optionnel)</label>
                  <Input
                    type="email"
                    value={sendEmail}
                    onChange={(e) => setSendEmail(e.target.value)}
                    placeholder="contact@clinique.ci (ou laisser vide pour utiliser la fiche clinique)"
                    data-testid="b2b-doc-send-email"
                  />
                </div>
              )}
              {(sendChannel === 'whatsapp' || sendChannel === 'both') && (
                <div>
                  <label className="text-xs text-zinc-500 mb-1 block">Téléphone WhatsApp (optionnel)</label>
                  <Input
                    type="tel"
                    value={sendPhone}
                    onChange={(e) => setSendPhone(e.target.value)}
                    placeholder="0700000000 (ou laisser vide)"
                    data-testid="b2b-doc-send-phone"
                  />
                </div>
              )}
              <p className="text-xs text-zinc-500">
                Email : PDF en pièce jointe via Resend.<br />
                WhatsApp : lien public sécurisé vers le document.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSendModal(null)} disabled={sendBusy}>
                Annuler
              </Button>
              <Button
                onClick={onSend}
                disabled={sendBusy}
                className="bg-[#1B4D3E] hover:bg-[#1B4D3E]/90 text-white"
                data-testid="b2b-doc-send-confirm"
              >
                {sendBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Envoyer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default B2BDocumentsSection;
